import { useState, type KeyboardEvent, type ReactNode } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

import Button from "components/Button";
import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import ProgressBar from "components/ProgressBar";
import ScreenChrome, {
  MAIN_GUTTER_CLASS,
  NARROW_MAIN_WIDTH_CLASS,
} from "components/ScreenChrome";

import { useLeadFlowAnalytics } from "../analytics";
import { stepCopy } from "../copy";
import { isStepComplete, progress, type QuestionStepId } from "../flow";
import { isContinueKey } from "./enterKey";
import type { StepProps } from "./types";

const FOOTER_ROW_CLASS =
  "mt-6 flex flex-wrap items-center justify-between gap-4";

/** Whether the visitor has pressed Continue on an incomplete step, which
 * is when its field errors start to show. */
export function useContinueGate(
  { answers, advance }: Pick<StepProps, "answers" | "advance">,
  step: QuestionStepId,
) {
  const analytics = useLeadFlowAnalytics();
  const [attempted, setAttempted] = useState(false);
  const complete = isStepComplete(answers, step);
  const tryContinue = () => {
    if (!complete) {
      analytics.stepInvalid(step);
      setAttempted(true);
      return;
    }
    advance();
  };
  return { attempted, tryContinue };
}

/** A field's error, shown only once the visitor has pressed Continue. */
export function gated(attempted: boolean, message: string | null) {
  return attempted ? message : null;
}

export function StepFooter({
  onBack,
  onContinue,
  busy = false,
  disabled = false,
  label = "Continue",
}: {
  onBack: (() => void) | null;
  /** Omitted for a step that has no Continue. */
  onContinue?: () => void;
  busy?: boolean;
  /** Continue is shown but cannot be pressed yet. */
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className={FOOTER_ROW_CLASS}>
      {onBack ? (
        <button type="button" className={LINK_BUTTON_CLASS} onClick={onBack}>
          Back
        </button>
      ) : (
        <span />
      )}
      {onContinue && (
        <Button
          variant="continue"
          className="ml-auto"
          disabled={disabled}
          isLoading={busy}
          icon={
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          }
          iconPosition="right"
          onClick={onContinue}
        >
          {label}
        </Button>
      )}
    </div>
  );
}

/** One question screen: the progress bar above, the heading, the body,
 * and the Back and Continue footer. Enter in a text input does what
 * Continue does. */
export default function StepFrame({
  answers,
  step,
  onBack,
  onContinue,
  busy,
  continueDisabled,
  continueLabel,
  children,
}: {
  answers: StepProps["answers"];
  step: QuestionStepId;
  onBack: StepProps["onBack"];
  onContinue?: () => void;
  busy?: boolean;
  continueDisabled?: boolean;
  continueLabel?: string;
  children: ReactNode;
}) {
  const onEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    const press = {
      key: event.key,
      composing: event.nativeEvent.isComposing,
      inputType: target instanceof HTMLInputElement ? target.type : null,
    };
    if (!isContinueKey(press) || !onContinue) return;
    event.preventDefault();
    if (!busy && !continueDisabled) onContinue();
  };
  const copy = stepCopy(answers, step);
  const { index, total } = progress(answers, step);
  return (
    <>
      <div className={`${MAIN_GUTTER_CLASS} ${NARROW_MAIN_WIDTH_CLASS} pt-6`}>
        <ProgressBar
          percentage={(index / total) * 100}
          label="Progress through the questions"
        />
      </div>
      <ScreenChrome
        kicker={copy.kicker}
        titleSize="compact"
        titleA={copy.titleA}
        titleB={copy.titleB}
        blurb={copy.blurb}
      >
        <div className="mt-7 w-full" onKeyDown={onEnter}>
          {children}
          <StepFooter
            onBack={onBack}
            onContinue={onContinue}
            busy={busy}
            disabled={continueDisabled}
            label={continueLabel}
          />
        </div>
      </ScreenChrome>
    </>
  );
}
